/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, July 29th 2024, 7:48:50 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TranslateIcon from "@mui/icons-material/Translate";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import { useTranslatedText } from "core";
import { APISERVICEPATHS } from "lib/api";
import { TranslatableContent, TLocales } from "lib/schemas";
import { useCopyTextToClipBoard } from "lib/utils";
import { RGBTriplets } from "theme/styles";
import { useTranslationAPI } from "translation/services";
import {
  DataTable,
  APIItemDisplayWrapper,
  StyledOSDialog,
  EmptyStateActionWrapper,
  Image,
  EmphasisText,
} from "ui-widgets";
import { TranslatableModelSelector } from "./shared";

export const TranslationsList = () => {
  const { useGetLanguages, usePatchTranslationText } = useTranslationAPI();
  const {
    locales,
    isMakingAPIRequest: isFetchingLocales,
    getSupportedLocales,
    httpResponseStatusCode: fetchLocalesStatusCode,
    isAPIError: fetchLocalesError,
  } = useGetLanguages();

  React.useEffect(() => {
    getSupportedLocales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [modelId, setModelId] = React.useState<string>();
  const [selectedRow, setSelectedRow] = React.useState<string>();

  const {
    copied: sourceTextCopied,
    copiedText: copiedSourceText,
    copyTextToClipBoard: copySourceText,
    setCopied,
  } = useCopyTextToClipBoard();

  const { patchTranslationText } = usePatchTranslationText();

  return (
    <APIItemDisplayWrapper
      showLoader={isFetchingLocales}
      httpResponseStatusCode={fetchLocalesStatusCode}
      isAPIError={fetchLocalesError}
    >
      <StyledOSDialog
        open={modelId === undefined}
        popUpTitle="Choose Model"
        popUpIcon={<TranslateIcon color="primary" />}
      >
        <TranslatableModelSelector onChooseModel={setModelId} selectedModelId={modelId} />
      </StyledOSDialog>
      <Snackbar
        open={sourceTextCopied}
        onClose={() => setCopied(false)}
        autoHideDuration={2000}
        message="Source Text Copied"
      />
      {Object.keys(locales).length === 0 || modelId === undefined ? null : (
        <DataTable<TranslatableContent>
          extraTools={<TranslatableModelSelector onChooseModel={setModelId} selectedModelId={modelId} />}
          title="Translations"
          headCells={[]}
          defaultOrderBy="field_name"
          initialRowsPerPage={20}
          dataSource={`${APISERVICEPATHS.manageTranslations}${modelId}/` as any}
          uniqueKey="source_text_id"
          searchKeys={[]}
          unit="Term"
          titleKey="source_text"
          listViewMode="custom"
          noDataMessage={<NoTranslationsMessage />}
        >
          {(rows) => {
            return (
              <TableContainer>
                <Table stickyHeader aria-labelledby="manageTranslations" size={"small"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Text</TableCell>
                      {Object.keys(locales).map((locale) => (
                        <TableCell key={locale}>{locales[locale]}</TableCell>
                      ))}
                      <TableCell>Source Language</TableCell>
                      <TableCell>Field</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row) => (
                      <ManageTranslationsRow
                        key={row.source_text_id}
                        onSaveTranslationText={(terms) => patchTranslationText(modelId, row.source_text_id, terms)}
                        row={row}
                        locales={locales}
                        copiedSourceText={copiedSourceText}
                        copySourceText={copySourceText}
                        selectedRow={selectedRow}
                        setSelectedRow={setSelectedRow}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          }}
        </DataTable>
      )}
    </APIItemDisplayWrapper>
  );
};

type ManageTranslationsRowProps = {
  row: TranslatableContent;
  locales: TLocales;
  copiedSourceText: string;
  copySourceText: (text: string) => void;
  onSaveTranslationText: (i18n: TranslatableContent["i18n"]) => void;
  setSelectedRow: (rowId: string) => void;
  selectedRow: string;
};

const ManageTranslationsRow = ({
  row,
  locales,
  copiedSourceText,
  copySourceText,
  onSaveTranslationText,
  setSelectedRow,
  selectedRow,
}: ManageTranslationsRowProps) => {
  const [transaltedTerms, setTranslatedTerms] = React.useState<TranslatableContent["i18n"]>(row.i18n);
  const onChangeTerm = (field: string, value: string) => {
    setTranslatedTerms((prev) => ({ ...prev, [field]: value }));
  };
  const [activeLocale, setActiveLocale] = React.useState<keyof TLocales>();
  const cellSharedStyle: React.CSSProperties = { maxWidth: "20rem", overflowWrap: "anywhere" };
  const onBlurTargetTranslationField = () => {
    onSaveTranslationText(transaltedTerms);
    setActiveLocale(undefined);
  };

  const showExpandTextArea = (localeData: string) => {
    if ([null, undefined].includes(localeData)) return false;
    return true;
  };

  return (
    <TableRow
      key={row.source_text_id}
      selected={copiedSourceText === row.source_text}
      onClick={() => setSelectedRow(row.source_text_id)}
    >
      <TableCell padding="none" sx={cellSharedStyle}>
        <Stack columnGap={2} direction={"row"}>
          <Tooltip title={<Typography>Copy</Typography>} placement="left-end" arrow>
            <IconButton onClick={() => copySourceText(row.source_text)}>
              <ContentCopyIcon color="secondary" fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.source_text}
        </Stack>
      </TableCell>
      {Object.keys(locales).map((locale) => {
        const localeFieldKey = `${row.field_name}_${locale}`;
        const localeData = transaltedTerms?.[localeFieldKey];
        return (
          <TableCell
            key={locale}
            padding="none"
            sx={
              activeLocale === locale && selectedRow === row.source_text_id
                ? { ...cellSharedStyle, background: `rgba(${RGBTriplets.primary}, 10%)` }
                : { ...cellSharedStyle }
            }
          >
            <TextField
              onClick={() => setActiveLocale(locale)}
              variant={"standard"}
              value={localeData}
              onBlur={onBlurTargetTranslationField}
              onChange={(event) => onChangeTerm(localeFieldKey, event.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <Tooltip title={<Typography>Start Typing...</Typography>} placement="left-end" arrow>
                    <TranslateIcon color="secondary" fontSize="small" sx={{ mr: 1, ml: 2 }} />
                  </Tooltip>
                ),
                endAdornment: showExpandTextArea(localeData) ? (
                  <StyledOSDialog
                    onClick={(event) => event.stopPropagation()}
                    popUpIcon={<TranslateIcon color="primary" />}
                    popUpTitle={`Translating \`${row.field_name}\` to ${locales[locale]}...`}
                    renderOpenButton={({ handleOpen }) => (
                      <Tooltip title={<Typography>Expand</Typography>}>
                        <IconButton onClick={handleOpen}>
                          <OpenInFullIcon color="primary" sx={{ fontSize: "12px" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  >
                    <TextField
                      rows={4}
                      multiline
                      value={localeData}
                      onBlur={onBlurTargetTranslationField}
                      onChange={(event) => onChangeTerm(localeFieldKey, event.target.value)}
                      fullWidth
                    />
                  </StyledOSDialog>
                ) : null,
              }}
              fullWidth
            />
          </TableCell>
        );
      })}
      <TableCell>{locales[row.source_language]}</TableCell>
      <TableCell>{row.field_name}</TableCell>
    </TableRow>
  );
};

const NoTranslationsMessage = () => {
  const LANG = useTranslatedText();
  return (
    <EmptyStateActionWrapper
      title={
        <Typography>
          <EmphasisText>{LANG.translationsNotFound}</EmphasisText>
        </Typography>
      }
      description={<Typography>{LANG.noContentToTranslate}</Typography>}
      illustrationGraphic={<Image src="/img/translation.png" alt="Onestop Admin" />}
    />
  );
};
